import Keycloak from "keycloak-js";
import { jwtDecode } from "jwt-decode";

var useNonce = true;
var getCookie = function (key: string) {
  var name = key + "=";
  var ca = document.cookie.split(";");
  for (var i = 0; i < ca.length; i++) {
    var c = ca[i];
    while (c.charAt(0) === " ") {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return "";
};

var setCookie = function (key: string, value: string, expirationDate: Date) {
  const cookie =
    // eslint-disable-next-line no-useless-concat
    key + "=" + value + "; " + "expires=" + expirationDate.toUTCString() + "; ";
  document.cookie = cookie;
};

var cookieExpiration = function (minutes: number) {
  var exp = new Date();
  exp.setTime(exp.getTime() + minutes * 60 * 1000);
  return exp;
};

const retrieveCookie = (state: string) => {
  const value = getCookie("kc-callback-" + state);
  setCookie("kc-callback-" + state, "", cookieExpiration(-100));
  if (value) {
    return JSON.parse(value);
  }
};

const retrieveLocalStorage = (state: string) => {
  const key = "kc-callback-" + state;
  let value = localStorage.getItem(key);
  if (value) {
    localStorage.removeItem(key);
    value = JSON.parse(value);
  }
  return value;
};

function createPromise(): Promise<void> {
  // Need to create a native Promise which also preserves the
  // interface of the custom promise type previously used by the API
  const p: any = {
    setSuccess: function (result: any) {
      p.resolve(result);
    },

    setError: function (result: any) {
      p.reject(result);
    }
  };

  p.promise = new Promise(function (resolve, reject) {
    p.resolve = resolve;
    p.reject = reject;
  });

  p.promise.success = function (callback: (value: any) => void) {
    this.then(function handleSuccess(value: any) {
      callback(value);
    }).catch((e: unknown) => {
      console.error("createPromise failed ", e);
    });

    return this;
  };

  p.promise.error = function (callback: (value: any) => void) {
    this.catch(function handleError(error: any) {
      callback(error);
    });

    return this;
  };

  return p;
}

function parseCallbackParams(paramsString: string, supportedParams: string[]) {
  const p = paramsString.split("&");
  const result = {
    paramsString: "",
    oauthParams: {}
  };
  for (let i = 0; i < p.length; i++) {
    const split = p[i].indexOf("=");
    const key = p[i].slice(0, split);
    if (supportedParams.indexOf(key) !== -1) {
      // @ts-ignore
      result.oauthParams[key] = p[i].slice(split + 1);
    } else {
      if (result.paramsString !== "") {
        result.paramsString += "&";
      }
      result.paramsString += p[i];
    }
  }
  return result;
}

function setToken(
  token: string,
  refreshToken: string,
  idToken: string,
  timeLocal: any,
  kc: Keycloak
) {
  // @ts-ignore
  if (kc.tokenTimeoutHandle) {
    // @ts-ignore
    clearTimeout(kc.tokenTimeoutHandle);
    // @ts-ignore
    kc.tokenTimeoutHandle = null;
  }

  if (refreshToken) {
    kc.refreshToken = refreshToken;
    kc.refreshTokenParsed = jwtDecode(refreshToken);
  } else {
    delete kc.refreshToken;
    delete kc.refreshTokenParsed;
  }

  if (idToken) {
    kc.idToken = idToken;
    // @ts-ignore
    kc.idTokenParsed = jwtDecode(idToken);
  } else {
    delete kc.idToken;
    delete kc.idTokenParsed;
  }

  if (token) {
    kc.token = token;
    // @ts-ignore
    kc.tokenParsed = jwtDecode(token);
    // @ts-ignore
    kc.sessionId = kc.tokenParsed.session_state;
    kc.authenticated = true;
    // @ts-ignore
    kc.subject = kc.tokenParsed.sub;
    // @ts-ignore
    kc.realmAccess = kc.tokenParsed.realm_access;
    // @ts-ignore
    kc.resourceAccess = kc.tokenParsed.resource_access;

    if (timeLocal) {
      // @ts-ignore
      kc.timeSkew = Math.floor(timeLocal / 1000) - kc.tokenParsed.iat;
    }

    if (kc.timeSkew != null) {
      console.log(
        "[KEYCLOAK] Estimated time difference between browser and server is " +
          kc.timeSkew +
          " seconds"
      );

      if (kc.onTokenExpired) {
        // @ts-ignore
        var expiresIn =
          // @ts-ignore
          (kc.tokenParsed["exp"] - new Date().getTime() / 1000 + kc.timeSkew) * 1000;
        console.info("[KEYCLOAK] Token expires in " + Math.round(expiresIn / 1000) + " s");
        if (expiresIn <= 0) {
          kc.onTokenExpired();
        } else {
          // @ts-ignore
          kc.tokenTimeoutHandle = setTimeout(kc.onTokenExpired, expiresIn);
        }
      }
    }
  } else {
    delete kc.token;
    delete kc.tokenParsed;
    delete kc.subject;
    delete kc.realmAccess;
    delete kc.resourceAccess;

    kc.authenticated = false;
  }
}

function processCallback(oauth: any, promise: any, kc: Keycloak) {
  var code = oauth.code;
  var error = oauth.error;
  var prompt = oauth.prompt;

  var timeLocal = new Date().getTime();

  if (oauth["kc_action_status"]) {
    kc.onActionUpdate && kc.onActionUpdate(oauth["kc_action_status"]);
  }

  if (error) {
    if (prompt !== "none") {
      var errorData = {
        error: error,
        error_description: oauth.error_description
      };
      kc.onAuthError && kc.onAuthError(errorData);
      promise && promise.setError(errorData);
    } else {
      promise && promise.setSuccess();
    }
    return;
  } else if (kc.flow !== "standard" && (oauth.access_token || oauth.id_token)) {
    authSuccess(oauth.access_token, oauth.refresh_token, oauth.id_token, true, kc);
  }

  if (kc.flow !== "implicit" && code && oauth.redirectUri) {
    var params = "code=" + code + "&grant_type=authorization_code";
    // @ts-ignore
    var url = kc.endpoints.token();

    var req = new XMLHttpRequest();
    req.open("POST", url, true);
    req.setRequestHeader("Content-type", "application/x-www-form-urlencoded");

    // @ts-ignore
    params += "&client_id=" + encodeURIComponent(kc.clientId);
    params += "&redirect_uri=" + oauth.redirectUri;

    if (oauth.pkceCodeVerifier) {
      params += "&code_verifier=" + oauth.pkceCodeVerifier;
    }

    req.withCredentials = true;

    req.onreadystatechange = function () {
      if (req.readyState === 4) {
        if (req.status === 200) {
          var tokenResponse = JSON.parse(req.responseText);
          authSuccess(
            tokenResponse["access_token"],
            tokenResponse["refresh_token"],
            tokenResponse["id_token"],
            kc.flow === "standard",
            kc
          );
          // scheduleCheckIframe();
        } else {
          // @ts-ignore
          kc.onAuthError && kc.onAuthError();
          promise && promise.setError();
        }
      }
    };

    req.send(params);
  }

  function authSuccess(
    accessToken: string,
    refreshToken: string,
    idToken: string,
    fulfillPromise: any,
    kc: Keycloak
  ) {
    timeLocal = (timeLocal + new Date().getTime()) / 2;

    setToken(accessToken, refreshToken!, idToken, timeLocal, kc);
    console.log("Auth success with params ", kc.idTokenParsed, accessToken, refreshToken, idToken);

    if (useNonce && kc.idTokenParsed && kc.idTokenParsed.nonce !== oauth.storedNonce) {
      console.info("[KEYCLOAK] Invalid nonce, clearing token");
      kc.clearToken();
      promise && promise.setError();
    } else {
      if (fulfillPromise) {
        console.log("onAuthSuccess");
        kc.onAuthSuccess && kc.onAuthSuccess();
        promise && promise.setSuccess();
      }
    }
  }
}

// @ts-ignore
function parseCallbackUrl(url: string, kc: Keycloak) {
  let supportedParams = [] as string[];
  switch (kc.flow) {
    case "standard":
      supportedParams = ["code", "state", "session_state", "kc_action_status"];
      break;
    case "implicit":
      supportedParams = [
        "access_token",
        "token_type",
        "id_token",
        "state",
        "session_state",
        "expires_in",
        "kc_action_status"
      ];
      break;
    case "hybrid":
      supportedParams = [
        "access_token",
        "token_type",
        "id_token",
        "code",
        "state",
        "session_state",
        "expires_in",
        "kc_action_status"
      ];
      break;
  }

  supportedParams.push("error");
  supportedParams.push("error_description");
  supportedParams.push("error_uri");

  const queryIndex = url.indexOf("?");
  const fragmentIndex = url.indexOf("#");

  let newUrl;
  let parsed;

  if (kc.responseMode === "query" && queryIndex !== -1) {
    newUrl = url.substring(0, queryIndex);
    parsed = parseCallbackParams(
      url.substring(queryIndex + 1, fragmentIndex !== -1 ? fragmentIndex : url.length),
      supportedParams
    );
    if (parsed.paramsString !== "") {
      newUrl += "?" + parsed.paramsString;
    }
    if (fragmentIndex !== -1) {
      newUrl += url.substring(fragmentIndex);
    }
  } else if (kc.responseMode === "fragment" && fragmentIndex !== -1) {
    newUrl = url.substring(0, fragmentIndex);
    parsed = parseCallbackParams(url.substring(fragmentIndex + 1), supportedParams);
    if (parsed.paramsString !== "") {
      newUrl += "#" + parsed.paramsString;
    }
  }

  if (parsed && parsed.oauthParams) {
    if (kc.flow === "standard" || kc.flow === "hybrid") {
      if (
        // @ts-ignore
        (parsed.oauthParams.code || parsed.oauthParams.error) &&
        // @ts-ignore
        parsed.oauthParams.state
      ) {
        // @ts-ignore
        parsed.oauthParams.newUrl = newUrl;
        return parsed.oauthParams;
      }
    } else if (kc.flow === "implicit") {
      if (
        // @ts-ignore
        (parsed.oauthParams.access_token || parsed.oauthParams.error) &&
        // @ts-ignore
        parsed.oauthParams.state
      ) {
        // @ts-ignore
        parsed.oauthParams.newUrl = newUrl;
        return parsed.oauthParams;
      }
    }
  }
}

function parseCallback(url: string, kc: Keycloak) {
  const oauth = parseCallbackUrl(url, kc) as any;
  if (!oauth) {
    return;
  }

  const oauthState =
    (retrieveLocalStorage(oauth.state) as any) ?? (retrieveCookie(oauth.state) as any);

  if (oauthState) {
    oauth.valid = true;
    oauth.redirectUri = oauthState.redirectUri;
    oauth.storedNonce = oauthState.nonce;
    oauth.prompt = oauthState.prompt;
    oauth.pkceCodeVerifier = oauthState.pkceCodeVerifier;
  }

  return oauth;
}

export { parseCallback, createPromise, processCallback };
